import { prelude, footer } from "./common";
import { buildScriptableWidget } from "@/utils/scriptableWidget";

const messagesBody = `  const m = data.messages || {};
  const recent = m.recent || [];
  const unread = m.unread_count || 0;
  const msgCount = isLarge ? 5 : 3;
  const shown = recent.slice(0, msgCount);
  // Scale type to fill the Large widget: fewer messages → bigger text, more preview lines
  const n = shown.length;
  const S = !isLarge ? { name: 11, pv: 10, time: 9, gap: 4, first: 3, rest: 1, dot: 6 }
          : n <= 1 ? { name: 17, pv: 15.5, time: 12, gap: 14, first: 12, rest: 5, dot: 9 }
          : n <= 2 ? { name: 16, pv: 14.5, time: 11.5, gap: 14, first: 6, rest: 5, dot: 9 }
          : n <= 3 ? { name: 15, pv: 13.5, time: 11, gap: 11, first: 4, rest: 3, dot: 8 }
          : n <= 4 ? { name: 14, pv: 12.5, time: 10.5, gap: 9, first: 3, rest: 2, dot: 8 }
          : { name: 12.5, pv: 11.5, time: 10, gap: 7, first: 3, rest: 1, dot: 7 };
  const who = (msg) => (msg.mine ? "You" : msg.from) + (msg.is_admin && msg.conversation ? " → " + msg.conversation : "");
  const { body } = card(w, C.green, MESSAGES_URL);
  cardTitle(body, "message.fill", C.green, "Recent Conversations", unread ? unread + " unread" : "All caught up");
  if (recent.length === 0) {
    body.addSpacer(8);
    text(body, "No messages yet", 13, C.faint);
  }
  shown.forEach((msg, i) => {
    body.addSpacer(S.gap);
    if (i > 0) { divider(body); body.addSpacer(S.gap); }
    const row = body.addStack();
    row.centerAlignContent();
    const dot = row.addStack();
    dot.size = new Size(S.dot, S.dot);
    dot.cornerRadius = S.dot / 2;
    dot.backgroundColor = msg.is_admin ? col(C.purple) : msg.read ? col(C.card, 0.18) : col(C.green);
    row.addSpacer(7);
    text(row, who(msg), S.name, msg.is_admin ? C.purple : C.text, { weight: msg.read ? "semi" : "bold" });
    row.addSpacer();
    text(row, ago(msg.sent_at), S.time, C.faint);
    body.addSpacer(2);
    const pv = body.addStack();
    pv.addSpacer(S.dot + 7);
    text(pv, (msg.text || msg.preview || "").trim(), S.pv, msg.is_admin || msg.read ? C.dim : C.text, { lines: i === 0 ? S.first : S.rest });
  });
`;

const notificationsBody = `  const n = data.notifications || {};
  const notifs = n.recent || [];
  const unreadCount = n.unread_count || 0;
  const notifCount = isLarge ? 8 : 3;
  const notifIcon = (t) => /clock_in/.test(t) ? "arrow.right.circle.fill" : /clock_out/.test(t) ? "arrow.left.circle.fill"
    : /w9|form|agreement|approval/.test(t) ? "doc.text.fill" : /application|applicant/.test(t) ? "person.badge.plus"
    : /payment|payroll/.test(t) ? "creditcard.fill" : /order|pull/.test(t) ? "shippingbox.fill" : "bell.fill";
  const { body } = card(w, C.amber, NOTIFICATIONS_URL);
  cardTitle(body, "bell.fill", C.amber, "Notifications", unreadCount ? unreadCount + " new" : "Nothing new");
  if (notifs.length === 0) {
    body.addSpacer(6);
    text(body, "No recent activity", 11, C.faint);
  }
  notifs.slice(0, notifCount).forEach((nt, i) => {
    body.addSpacer(i === 0 ? 5 : 4);
    const row = body.addStack();
    row.centerAlignContent();
    icon(row, notifIcon(nt.type || ""), 10, nt.read ? C.faint : C.amber);
    row.addSpacer(6);
    const msgTxt = (nt.message || "").trim();
    const nameIn = nt.employee_name && msgTxt.toLowerCase().includes(nt.employee_name.toLowerCase());
    const txt = (nt.employee_name && !nameIn ? nt.employee_name + ": " : "") + msgTxt;
    text(row, txt, 10, nt.read ? C.dim : C.text, { weight: nt.read ? "regular" : "semi", lines: i === 0 ? 3 : 1, scale: 0.9 });
    row.addSpacer(6);
    text(row, ago(nt.created_at), 9, C.faint);
  });
`;

const payrollBody = `  const p = data.payroll || {};
  const filterEmps = (arr) => (arr || []).filter((e) => !HIDE_TEAM.includes(e.name));
  // API: payroll.history = [current, prior 1, prior 2]
  const periods = (p.history || [{ period_start: p.period_start, period_end: p.period_end, amount: p.amount, hours: p.hours, by_employee: p.by_employee }])
    .map((per) => ({ ...per, by_employee: filterEmps(per.by_employee) }));
  const { body } = card(w, C.purple, SITE_URL);
  // Scale type to fill the Large widget: fewer rows → bigger text
  const rows = periods.slice(0, 3).reduce((n, per) => n + 1 + Math.max(1, (per.by_employee || []).length), 0);
  const big = isLarge && rows <= 9;
  const S = big ? { hdr: 12, total: 15, name: 14, meta: 11.5, amt: 15, gap: 7, sect: 12 }
          : isLarge ? { hdr: 10.5, total: 13, name: 12, meta: 10, amt: 13, gap: 5, sect: 9 }
          : { hdr: 8.5, total: 9, name: 9.5, meta: 8.5, amt: 9.5, gap: 2, sect: 5 };
  periods.slice(0, 3).forEach((per, pIdx) => {
    if (pIdx > 0) {
      body.addSpacer(S.sect);
      divider(body);
      body.addSpacer(S.sect);
    }
    const pHeader = body.addStack();
    pHeader.centerAlignContent();
    const labelTxt = pIdx === 0 ? "CURRENT PERIOD" : "PRIOR PERIOD " + pIdx;
    text(pHeader, labelTxt + " · " + mmdd(per.period_start) + " – " + mmdd(per.period_end), S.hdr, pIdx === 0 ? C.purple : C.dim, { weight: "bold" });
    pHeader.addSpacer();
    text(pHeader, exact(per.amount), S.total, C.text, { weight: "heavy" });
    const emps = per.by_employee || [];
    if (emps.length === 0) {
      body.addSpacer(S.gap);
      text(body, "No hours logged", S.meta, C.faint);
    } else {
      emps.forEach((e) => {
        body.addSpacer(S.gap);
        const eRow = body.addStack();
        eRow.centerAlignContent();
        text(eRow, e.name, S.name, C.text, { weight: "semi" });
        eRow.addSpacer();
        // Show the configured rate (API "rate"); only derive amount÷hours when a period mixed rates
        const hourlyRate = e.rate && !e.multi_rate ? exact(e.rate) + "/hr"
          : e.hours > 0 ? "≈" + exact((e.amount || 0) / e.hours) + "/hr" : "$0.00/hr";
        text(eRow, formatHoursMins(e.hours) + " @ " + hourlyRate, S.meta, C.dim);
        eRow.addSpacer(8);
        text(eRow, exact(e.amount), S.amt, C.purple, { weight: "bold" });
      });
    }
  });
  body.addSpacer(S.gap);
`;

const teamBody = `  const team = (data.team || []).filter((t) => !HIDE_TEAM.includes(t.name));
  const onNowCount = team.filter((t) => t.clocked_in).length;
  const { body } = card(w, C.cyan, SITE_URL);
  cardTitle(body, "person.2.fill", C.cyan, "Team Shifts", onNowCount ? onNowCount + " on the clock" : "This & last pay period");
  body.addSpacer(6);
  if (team.length === 0) {
    text(body, "No active team members", 10, C.faint);
  } else {
    const shown = team.slice(0, 4);
    // Fit the fixed widget height: budget shift lines per period per employee
    const perPeriod = isLarge ? Math.max(1, Math.floor((24 - 3 * shown.length) / (2 * shown.length))) : 1;
    shown.forEach((t, i) => {
      if (i > 0) {
        body.addSpacer(4);
        divider(body);
        body.addSpacer(4);
      }
      const empRow = body.addStack();
      empRow.centerAlignContent();
      const dot = empRow.addStack();
      dot.size = new Size(6, 6);
      dot.cornerRadius = 3;
      dot.backgroundColor = t.clocked_in ? col(C.green) : col(C.card, 0.18);
      empRow.addSpacer(6);
      text(empRow, t.name, 10.5, C.text, { weight: "bold" });
      empRow.addSpacer();
      if (t.clocked_in) text(empRow, "ON CLOCK" + (t.clock_in_time ? " · since " + t.clock_in_time : ""), 8.5, C.green, { weight: "bold" });
      // API: team[].periods.current / .previous = { start, end, hours, amount, shifts[] }
      const per = t.periods || {};
      [["THIS PERIOD", per.current], ["LAST PERIOD", per.previous]].forEach(([label, pd]) => {
        if (!pd) return;
        body.addSpacer(3);
        const ph = body.addStack();
        ph.centerAlignContent();
        ph.addSpacer(12);
        text(ph, label + " (" + mmdd(pd.start) + " – " + mmdd(pd.end) + ")", 8, C.cyan, { weight: "bold" });
        ph.addSpacer();
        text(ph, formatHoursMins(pd.hours) + " · " + exact(pd.amount), 8.5, C.dim, { weight: "semi" });
        const shifts = pd.shifts || [];
        if (shifts.length === 0) {
          body.addSpacer(1);
          const none = body.addStack();
          none.addSpacer(18);
          text(none, "No shifts", 8.5, C.faint);
        }
        shifts.slice(0, perPeriod).forEach((s) => {
          body.addSpacer(1);
          const shiftRow = body.addStack();
          shiftRow.centerAlignContent();
          shiftRow.addSpacer(18);
          text(shiftRow, mmdd(s.date) + (s.time ? " · " + s.time : ""), 9, C.dim);
          shiftRow.addSpacer();
          text(shiftRow, formatHoursMins(s.hours), 9, C.text, { weight: "semi" });
          shiftRow.addSpacer(6);
          text(shiftRow, exact(s.amount), 9, C.cyan, { weight: "semi" });
        });
        if (shifts.length > perPeriod) {
          const more = body.addStack();
          more.addSpacer(18);
          text(more, "+" + (shifts.length - perPeriod) + " more", 8, C.faint);
        }
      });
    });
  }
`;

const make = (meta, body) => (host, key) => prelude({ host, key, ...meta }) + body + footer;

export const WIDGET_SCRIPTS = [
  { id: "overview", name: "Overview", desc: "Messages, team & payroll in one widget", build: (host, key) => buildScriptableWidget(`https://${host}`, key) },
  { id: "messages", name: "Messages", desc: "Last 5 messages, text sized to fill the widget · tap → Messages", build: make({ title: "Messages", tapUrl: "MESSAGES_URL", comment: "Tap the widget → opens Messages." }, messagesBody) },
  { id: "notifications", name: "Notifications", desc: "Last 8 activity notifications · tap → Notifications", build: make({ title: "Notifications", tapUrl: "NOTIFICATIONS_URL", comment: "Tap the widget → opens Notifications." }, notificationsBody) },
  { id: "payroll", name: "Payroll History", desc: "Current + 2 prior pay periods, per employee", build: make({ title: "Payroll History", tapUrl: "SITE_URL", comment: "Tap the widget → opens Admin." }, payrollBody) },
  { id: "team", name: "Team Shifts", desc: "Who's on the clock + every shift this & last pay period", build: make({ title: "Team Shifts", tapUrl: "SITE_URL", comment: "Tap the widget → opens Admin." }, teamBody) },
];
